export interface ExternalWorkspace {
  id: string;
  kind: 'student' | 'group' | 'demo';
  generation: number;
  companyId: string;
  companyName: string;
  recipientName: string;
  isTest: boolean;
  activityId: string | null;
  activityTitle: string | null;
}
export interface ExternalEnvironment {
  id: string;
  workspaceId: string;
  templateId: string;
  templateLabel: string;
  observed?: unknown;
  expectedVersion?: string | null;
  label: string;
  status: 'queued' | 'creating' | 'ready' | 'stopped' | 'failed' | 'archived';
  revision: number;
  hostname: string | null;
  jobs: {
    id: string;
    action: string;
    status: 'queued' | 'running' | 'succeeded' | 'failed';
    error: string | null;
    createdAt: string;
  }[];
  backupSchedule?: ExternalBackupSchedule | null;
  backups: { id: string; createdAt: string; sizeBytes: number; restoredAt: string | null }[];
}
export interface ExternalOverview {
  workspaces: ExternalWorkspace[];
  environments: ExternalEnvironment[];
}
export type RecipientKind = 'student' | 'group' | 'demo' | 'test';
export function recipientsFor(
  workspaces: ExternalWorkspace[],
  companyId: string,
  kind: RecipientKind,
  activityId?: string | null,
): ExternalWorkspace[] {
  return workspaces.filter(
    (workspace) =>
      workspace.companyId === companyId &&
      (activityId === undefined || workspace.activityId === activityId) &&
      (kind === 'test'
        ? workspace.kind === 'student' && workspace.isTest
        : workspace.kind === kind && !workspace.isTest),
  );
}
/** Stable after a connection loss or tab reload; a successful acknowledgement releases the key. */
export function externalRequestKey(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  scope: string,
  makeId: () => string,
): string {
  const existing = storage.getItem(scope);
  if (
    existing &&
    /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(existing)
  )
    return existing;
  const id = makeId();
  storage.setItem(scope, id);
  return id;
}

export interface ExternalBackupSchedule {
  retentionDays?: 7 | 30 | 90 | null;
  intervalHours: 24 | 168 | null;
  nextDueAt: string | null;
  revision: number;
}
export interface BackupScheduleDraft {
  retentionDays: 7 | 30 | 90 | null;
  intervalHours: 24 | 168 | null;
  revision: number;
}
/** Preserve the original revision across reloads so a second teacher's change cannot be overwritten silently. */
export function readBackupScheduleDraft(
  raw: string | null,
  policy?: ExternalBackupSchedule | null,
): BackupScheduleDraft {
  try {
    const value: unknown = JSON.parse(raw ?? 'null');
    if (
      typeof value === 'object' &&
      value !== null &&
      'intervalHours' in value &&
      'revision' in value &&
      (value.intervalHours === null || value.intervalHours === 24 || value.intervalHours === 168) &&
      typeof value.revision === 'number' &&
      Number.isInteger(value.revision) &&
      value.revision >= 0
    )
      return {
        intervalHours: value.intervalHours,
        revision: value.revision,
        retentionDays:
          'retentionDays' in value &&
          (value.retentionDays === 7 ||
            value.retentionDays === 30 ||
            value.retentionDays === 90 ||
            value.retentionDays === null)
            ? value.retentionDays
            : (policy?.retentionDays ?? null),
      };
  } catch {
    /* A damaged or unavailable local draft must not prevent access to the saved policy. */
  }
  return {
    intervalHours: policy?.intervalHours ?? null,
    revision: policy?.revision ?? 0,
    retentionDays: policy?.retentionDays ?? null,
  };
}
